import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Counter animation hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

const Hero = () => {
  const moves = useCountUp(10000, 2000);
  const movers = useCountUp(500, 1800);
  const rating = useCountUp(49, 1500); // 49 will display as 4.9
  const support = useCountUp(24, 1200);
  const rotatingWords = useMemo(() => ['ANYTHING', 'ANYWHERE', 'ANYTIME'], []);
  const [activeWord, setActiveWord] = useState(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setActiveWord((currentWord) => (currentWord + 1) % rotatingWords.length);
    }, 1900);

    return () => clearTimeout(timeoutId);
  }, [activeWord, rotatingWords.length]);

  return (
    <section className="relative min-h-[600px] md:min-h-[700px] lg:min-h-[800px] flex items-center overflow-hidden pb-32 sm:pb-32 md:pb-24 lg:pb-16">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(/heroimage.jpg)',
          filter: 'blur(3px)',
        }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(15,23,42,0.72),rgba(15,23,42,0.48)_48%,rgba(15,23,42,0.34))]" />
      <div className="absolute inset-0 bg-black/25" />

      <div className="container mx-auto px-4 relative z-10 pt-4 md:pt-0">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          <div className="text-white">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 md:px-4 md:py-2 mb-4 md:mb-6 shadow-[0_12px_34px_rgba(0,0,0,0.18)]">
              <svg className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs md:text-sm font-medium">Trusted by 10,000+ Happy Customers</span>
            </div>

            <h1 className="mb-4 text-5xl font-black leading-[0.96] tracking-normal sm:text-6xl md:mb-6 lg:text-7xl">
              <span className="block">Move</span>
              <span className="relative mt-2 block h-[1.05em] overflow-hidden text-primary sm:h-[1.08em]">
                {rotatingWords.map((word, index) => (
                  <motion.span
                    key={word}
                    className="absolute left-0 top-0 block whitespace-nowrap"
                    initial={{ opacity: 0, y: 70 }}
                    animate={
                      activeWord === index
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: activeWord > index ? -76 : 76 }
                    }
                    transition={{
                      type: 'spring',
                      stiffness: 70,
                      damping: 16,
                      mass: 0.75,
                    }}
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
              <span className="block text-white">with confidence.</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl mb-6 md:mb-8 text-gray-100 leading-relaxed max-w-xl">
              Compare trusted movers, get free quotes, and choose the right team for your home, office, or long-distance move.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-6 md:mb-0">
              <Link to="/quote" className="w-full sm:w-auto">
                <span className="inline-flex min-h-[52px] w-full items-center justify-center rounded-xl bg-primary px-6 text-base font-bold text-white shadow-[0_14px_34px_rgba(235,152,19,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-[0_18px_42px_rgba(235,152,19,0.36)] md:min-h-[56px] md:text-lg sm:w-auto">
                  Get Free Quote
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
              <Link to="/about" className="w-full sm:w-auto">
                <span className="inline-flex min-h-[52px] w-full items-center justify-center rounded-xl border border-white/35 bg-white/10 px-6 text-base font-bold text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/70 hover:bg-white/18 hover:text-white hover:shadow-[0_14px_34px_rgba(0,0,0,0.22)] md:min-h-[56px] md:text-lg sm:w-auto">
                  Learn More
                </span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
            <div ref={moves.ref} className="bg-white/90 backdrop-blur-sm rounded-lg md:rounded-xl p-2.5 sm:p-3 md:p-4 shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.3)] transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer group">
              <div className="text-base sm:text-xl md:text-2xl font-bold text-green-600 mb-0.5 sm:mb-1 group-hover:scale-110 transition-transform duration-300">{moves.count.toLocaleString()}+</div>
              <div className="text-gray-600 text-[10px] sm:text-[11px] md:text-xs">Successful Moves</div>
            </div>
            <div ref={movers.ref} className="bg-white/90 backdrop-blur-sm rounded-lg md:rounded-xl p-2.5 sm:p-3 md:p-4 shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.3)] transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer group">
              <div className="text-base sm:text-xl md:text-2xl font-bold text-blue-600 mb-0.5 sm:mb-1 group-hover:scale-110 transition-transform duration-300">{movers.count}+</div>
              <div className="text-gray-600 text-[10px] sm:text-[11px] md:text-xs">Verified Movers</div>
            </div>
            <div ref={rating.ref} className="bg-white/90 backdrop-blur-sm rounded-lg md:rounded-xl p-2.5 sm:p-3 md:p-4 shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.3)] transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer group">
              <div className="text-base sm:text-xl md:text-2xl font-bold text-primary mb-0.5 sm:mb-1 group-hover:scale-110 transition-transform duration-300">{(rating.count / 10).toFixed(1)}★</div>
              <div className="text-gray-600 text-[10px] sm:text-[11px] md:text-xs">Average Rating</div>
            </div>
            <div ref={support.ref} className="bg-white/90 backdrop-blur-sm rounded-lg md:rounded-xl p-2.5 sm:p-3 md:p-4 shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.3)] transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer group">
              <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1 group-hover:scale-110 transition-transform duration-300">{support.count}/7</div>
              <div className="text-gray-600 text-[10px] sm:text-[11px] md:text-xs">Support Available</div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 -mb-px">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="block w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
        </svg>
      </div>

      <div className="absolute bottom-10 md:bottom-8 left-1/2 transform -translate-x-1/2 flex flex-row items-center justify-center space-x-3 sm:space-x-4 md:space-x-8 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 shadow-xl z-10 w-auto max-w-[95%]">
        <div className="flex items-center space-x-1.5 sm:space-x-2">
          <svg className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-[11px] sm:text-xs md:text-sm font-semibold text-gray-800">100% Verified</span>
        </div>
        <div className="w-px h-4 bg-gray-300" />
        <div className="flex items-center space-x-1.5 sm:space-x-2">
          <svg className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
          </svg>
          <span className="text-[11px] sm:text-xs md:text-sm font-semibold text-gray-800">Fast Response</span>
        </div>
        <div className="w-px h-4 bg-gray-300" />
        <div className="flex items-center space-x-1.5 sm:space-x-2">
          <svg className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
          </svg>
          <span className="text-[11px] sm:text-xs md:text-sm font-semibold text-gray-800">Best Prices</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
